export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => Promise<string | null>;
}

function formatApiErrorDetails(details: unknown): string | undefined {
  if (typeof details === 'string' && details.trim().length > 0) {
    return details.trim();
  }
  if (Array.isArray(details)) {
    const parts = details
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
    if (parts.length > 0) {
      return parts.join('; ');
    }
  }
  return undefined;
}

/** Normalize any valid HeadersInit form into a plain Record. */
function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
}

export class ApiClient {
  private baseUrl: string;
  private getToken?: () => Promise<string | null>;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.getToken = config.getToken;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const normalizedHeaders = normalizeHeaders(options.headers);
    const hasContentTypeHeader = Object.keys(normalizedHeaders).some(
      (header) => header.toLowerCase() === 'content-type',
    );
    const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: Record<string, string> = { ...normalizedHeaders };
    if (!isFormDataBody && !hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const url = new URL(path, this.baseUrl);

    const response = await fetch(url.toString(), {
      ...options,
      headers,
    });

    if (!response.ok) {
      let details: string | undefined;
      try {
        const payload = (await response.clone().json()) as {
          error?: string;
          message?: string;
          details?: unknown;
        };
        const base = payload.error ?? payload.message;
        const extra = formatApiErrorDetails(payload.details);
        details = base && extra ? `${base} - ${extra}` : (base ?? extra);
      } catch {
        try {
          details = (await response.clone().text()).trim();
        } catch {
          details = undefined;
        }
      }

      throw new Error(
        details && details.length > 0
          ? `API error: ${response.status} ${response.statusText} - ${details}`
          : `API error: ${response.status} ${response.statusText}`,
      );
    }

    const contentLength = response.headers.get('content-length');
    if (response.status === 204 || contentLength === '0') {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  private async requestBlob(path: string, options: RequestInit = {}): Promise<Blob> {
    const headers: Record<string, string> = normalizeHeaders(options.headers);

    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const url = new URL(path, this.baseUrl);
    const response = await fetch(url.toString(), {
      ...options,
      headers,
    });

    if (!response.ok) {
      let details: string | undefined;
      try {
        const payload = (await response.clone().json()) as {
          error?: string;
          message?: string;
          details?: unknown;
        };
        const base = payload.error ?? payload.message;
        const extra = formatApiErrorDetails(payload.details);
        details = base && extra ? `${base} - ${extra}` : (base ?? extra);
      } catch {
        try {
          details = (await response.clone().text()).trim();
        } catch {
          details = undefined;
        }
      }

      throw new Error(
        details && details.length > 0
          ? `API error: ${response.status} ${response.statusText} - ${details}`
          : `API error: ${response.status} ${response.statusText}`,
      );
    }

    return response.blob();
  }

  async healthCheck() {
    return this.request<{ status: string; timestamp: string }>('/health');
  }

  async getSettings() {
    return this.request<{ data: import('./types').SystemConfig[] }>('/api/settings');
  }

  async getAiSettings() {
    return this.request<{ data: import('./types').AiSettingsResponse }>('/api/ai/settings');
  }

  async saveAiSettings(payload: {
    modelParams?: Partial<import('./types').AiModelParams>;
    models?: Partial<import('./types').AiModelRouting>;
    prompts?: Partial<import('./types').AiPromptTemplates>;
    features?: Partial<import('./types').AiFeatureFlags>;
  }) {
    return this.request<{ status: string; data: import('./types').AiSettings }>(
      '/api/ai/settings',
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async saveSetting(config: Partial<import('./types').SystemConfig> & { configKey: string }) {
    return this.request<{ status: string }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async getConversations() {
    return this.request<{ data: import('./types').Conversation[] }>('/api/conversations');
  }

  async getMe() {
    return this.request<{ data: import('./types').User }>('/api/me');
  }

  async getMyPreferences() {
    return this.request<{ data: import('./types').UserPreference[] }>('/api/me/preferences');
  }

  async saveMyPreference(
    payload: { key: string; value: unknown },
    options?: {
      keepalive?: boolean;
    },
  ) {
    return this.request<{ data: import('./types').UserPreference }>('/api/me/preferences', {
      method: 'PUT',
      body: JSON.stringify(payload),
      keepalive: options?.keepalive === true,
    });
  }

  async getInboundVoiceSettings() {
    return this.request<{ data: import('./types').InboundVoiceSettings }>(
      '/api/team/inbound-voice-settings',
    );
  }

  async saveInboundVoiceSettings(payload: Partial<import('./types').InboundVoiceSettings>) {
    return this.request<{ data: import('./types').InboundVoiceSettings }>(
      '/api/team/inbound-voice-settings',
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async getVoiceMediaAssets() {
    return this.request<{ data: import('./types').VoiceMediaAsset[] }>('/api/team/voice-media');
  }

  async createVoiceMediaAsset(payload: {
    label: string;
    purpose: import('./types').VoiceMediaAssetPurpose;
    storageProvider: import('./types').VoiceMediaStorageProvider;
    objectKey?: string | null;
    externalUrl?: string | null;
    contentType?: string | null;
    durationSeconds?: number | null;
  }) {
    return this.request<{ data: import('./types').VoiceMediaAsset }>('/api/team/voice-media', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async uploadVoiceMediaAsset(payload: {
    label: string;
    purpose: import('./types').VoiceMediaAssetPurpose;
    file: File;
  }) {
    const form = new FormData();
    form.set('label', payload.label);
    form.set('purpose', payload.purpose);
    form.set('file', payload.file);

    return this.request<{ data: import('./types').VoiceMediaAsset }>(
      '/api/team/voice-media/upload',
      {
        method: 'POST',
        body: form,
      },
    );
  }

  async updateVoiceMediaAsset(
    assetId: string,
    payload: Partial<{
      label: string;
      purpose: import('./types').VoiceMediaAssetPurpose;
      storageProvider: import('./types').VoiceMediaStorageProvider;
      objectKey: string | null;
      externalUrl: string | null;
      contentType: string | null;
      durationSeconds: number | null;
    }>,
  ) {
    return this.request<{ data: import('./types').VoiceMediaAsset }>(
      `/api/team/voice-media/${assetId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async getMyVoiceAvailability() {
    return this.request<{ data: import('./types').AgentVoiceAvailability }>(
      '/api/me/voice-availability',
    );
  }

  async saveMyVoiceAvailability(payload: { manualAvailability: boolean }) {
    return this.request<{ data: import('./types').AgentVoiceAvailability }>(
      '/api/me/voice-availability',
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async sendVoicePresenceHeartbeat(payload: {
    sessionKey: string;
    provider: import('./types').VoiceProvider;
    clientIdentity: string;
    deviceLabel?: string | null;
    registrationStatus: import('./types').AgentVoiceRegistrationStatus;
    expiresAt?: string | null;
    metadata?: Record<string, unknown> | null;
  }) {
    return this.request<{ data: import('./types').AgentVoiceAvailability }>(
      '/api/me/voice-availability/heartbeat',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  }

  async getUsers() {
    return this.request<{ data: import('./types').TeamUser[] }>('/api/users');
  }

  async getContacts(search?: string) {
    const query = search ? `?q=${encodeURIComponent(search)}` : '';
    return this.request<{ data: import('./types').ContactListItem[] }>(`/api/contacts${query}`);
  }

  async getCrmFields() {
    return this.request<{ data: import('./types').CrmFieldDefinition[] }>('/api/crm/fields');
  }

  async createCrmField(payload: {
    key: string;
    label: string;
    fieldType: import('./types').CrmFieldType;
    required?: boolean;
    icon?: string | null;
    color?: string | null;
    isActive?: boolean;
    position?: number;
    options?: Array<{
      value: string;
      label: string;
      color?: string | null;
      position?: number;
    }>;
  }) {
    return this.request<{ data: import('./types').CrmFieldDefinition }>('/api/crm/fields', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateCrmField(
    fieldId: string,
    payload: Partial<{
      key: string;
      label: string;
      fieldType: import('./types').CrmFieldType;
      required: boolean;
      icon: string | null;
      color: string | null;
      isActive: boolean;
      position: number;
    }>,
  ) {
    return this.request<{ data: import('./types').CrmFieldDefinition }>(
      `/api/crm/fields/${fieldId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async deleteCrmField(fieldId: string) {
    return this.request<{ status: string }>(`/api/crm/fields/${fieldId}`, {
      method: 'DELETE',
    });
  }

  async createCrmFieldOption(
    fieldId: string,
    payload: {
      value: string;
      label: string;
      color?: string | null;
      position?: number;
    },
  ) {
    return this.request<{ data: import('./types').CrmFieldOption }>(
      `/api/crm/fields/${fieldId}/options`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  }

  async updateCrmFieldOption(
    fieldId: string,
    optionId: string,
    payload: Partial<{
      value: string;
      label: string;
      color: string | null;
      position: number;
    }>,
  ) {
    return this.request<{ data: import('./types').CrmFieldOption }>(
      `/api/crm/fields/${fieldId}/options/${optionId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async deleteCrmFieldOption(fieldId: string, optionId: string) {
    return this.request<{ status: string }>(`/api/crm/fields/${fieldId}/options/${optionId}`, {
      method: 'DELETE',
    });
  }

  async getCrmViews() {
    return this.request<{ data: import('./types').CrmSmartView[] }>('/api/crm/views');
  }

  async createCrmView(payload: {
    name: string;
    isDefault?: boolean;
    isShared?: boolean;
    config: import('./types').CrmViewConfig;
  }) {
    return this.request<{ data: import('./types').CrmSmartView }>('/api/crm/views', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateCrmView(
    viewId: string,
    payload: Partial<{
      name: string;
      isDefault: boolean;
      isShared: boolean;
      config: import('./types').CrmViewConfig;
    }>,
  ) {
    return this.request<{ data: import('./types').CrmSmartView }>(`/api/crm/views/${viewId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteCrmView(viewId: string) {
    return this.request<{ status: string }>(`/api/crm/views/${viewId}`, {
      method: 'DELETE',
    });
  }

  async getCrmPipelines() {
    return this.request<{
      data: Array<import('./types').CrmPipeline & { stages: import('./types').CrmPipelineStage[] }>;
    }>('/api/crm/pipelines');
  }

  async saveCrmPipeline(
    payload: Partial<{
      id: string;
      name: string;
      isDefault: boolean;
      isActive: boolean;
    }>,
  ) {
    const method = payload.id ? 'PUT' : 'POST';
    return this.request<{ data: import('./types').CrmPipeline }>('/api/crm/pipelines', {
      method,
      body: JSON.stringify(payload),
    });
  }

  async saveCrmPipelineStage(
    pipelineId: string,
    payload: Partial<{
      id: string;
      key: string;
      label: string;
      color: string | null;
      position: number;
      isClosed: boolean;
      suggestTaskOnEnter: boolean;
      suggestTaskPriority: import('./types').TaskPriority | null;
      suggestTaskDueHours: number | null;
      decayWarnHours: number | null;
      decayCriticalHours: number | null;
    }>,
  ) {
    const method = payload.id ? 'PUT' : 'POST';
    return this.request<{ data: import('./types').CrmPipelineStage }>(
      `/api/crm/pipelines/${pipelineId}/stages`,
      {
        method,
        body: JSON.stringify(payload),
      },
    );
  }

  async deleteCrmPipelineStage(pipelineId: string, stageId: string) {
    return this.request<{ status: string }>(`/api/crm/pipelines/${pipelineId}/stages/${stageId}`, {
      method: 'DELETE',
    });
  }

  async getTaskStatuses() {
    return this.request<{ data: import('./types').TaskStatus[] }>('/api/crm/task-statuses');
  }

  async createTaskStatus(payload: {
    key: string;
    label: string;
    color?: string | null;
    position?: number;
    isTerminal?: boolean;
  }) {
    return this.request<{ data: import('./types').TaskStatus }>('/api/crm/task-statuses', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateTaskStatus(
    statusId: string,
    payload: Partial<{
      key: string;
      label: string;
      color: string | null;
      position: number;
      isTerminal: boolean;
    }>,
  ) {
    return this.request<{ data: import('./types').TaskStatus }>(
      `/api/crm/task-statuses/${statusId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async deleteTaskStatus(statusId: string) {
    return this.request<{ status: string }>(`/api/crm/task-statuses/${statusId}`, {
      method: 'DELETE',
    });
  }

  async queryCrmContacts(payload: {
    pipelineId?: string | null;
    columns?: string[];
    filters?: import('./types').CrmFilterRule[];
    sorts?: import('./types').CrmSortRule[];
    limit?: number;
  }) {
    return this.request<{
      data: {
        rows: import('./types').CrmContactRow[];
        board: import('./types').CrmBoardColumn[];
      };
    }>('/api/crm/contacts/query', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateCrmContactFields(
    contactId: string,
    payload: {
      values: Array<{
        definitionId: string;
        valueText?: string | null;
        valueNumber?: number | null;
        valueDate?: string | null;
        valueBoolean?: boolean | null;
        valueJson?: string | null;
      }>;
    },
  ) {
    return this.request<{ data: import('./types').CrmFieldValue[] }>(
      `/api/crm/contacts/${contactId}/fields`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async moveCrmContactStage(
    contactId: string,
    payload: {
      pipelineId: string;
      stageId: string;
    },
  ) {
    return this.request<{
      data: {
        entry: import('./types').CrmPipelineEntry;
        suggestion: null | {
          title: string;
          priority: import('./types').TaskPriority;
          dueAt: string | null;
        };
      };
    }>(`/api/crm/contacts/${contactId}/stage`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async createContact(payload: {
    phoneNumber: string;
    displayName?: string;
    email?: string;
    notes?: string;
  }) {
    return this.request<{ data: import('./types').Contact }>('/api/contacts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateContact(
    contactId: string,
    payload: { displayName?: string; email?: string; notes?: string; phoneNumber?: string },
  ) {
    return this.request<{ data: import('./types').Contact }>(`/api/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteContact(contactId: string) {
    return this.request<{ status: string }>(`/api/contacts/${contactId}`, {
      method: 'DELETE',
    });
  }

  async getContactTimeline(contactId: string) {
    return this.request<{ data: import('./types').ConversationEvent[] }>(
      `/api/contacts/${contactId}/timeline`,
    );
  }

  async getTasks(options?: {
    contactId?: string;
    assignedUserId?: string;
    includeCompleted?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options?.contactId) params.set('contactId', options.contactId);
    if (options?.assignedUserId) params.set('assignedUserId', options.assignedUserId);
    if (options?.includeCompleted) params.set('includeCompleted', 'true');
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.request<{ data: import('./types').Task[] }>(`/api/crm/tasks${query}`);
  }

  async createTask(payload: {
    contactId?: string | null;
    title: string;
    description?: string | null;
    priority?: import('./types').TaskPriority;
    statusId?: string;
    assignedUserId?: string | null;
    dueAt?: string | null;
    sourceType?: import('./types').TaskSourceType;
    sourceRefId?: string | null;
    position?: number;
  }) {
    return this.request<{ data: import('./types').Task }>('/api/crm/tasks', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateTask(
    taskId: string,
    payload: Partial<{
      title: string;
      description: string | null;
      priority: import('./types').TaskPriority;
      statusId: string;
      assignedUserId: string | null;
      dueAt: string | null;
      completedAt: string | null;
      position: number;
    }>,
  ) {
    return this.request<{ data: import('./types').Task }>(`/api/crm/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteTask(taskId: string) {
    return this.request<{ status: string }>(`/api/crm/tasks/${taskId}`, {
      method: 'DELETE',
    });
  }

  async moveTaskStatus(
    taskId: string,
    payload: {
      statusId: string;
      position?: number;
    },
  ) {
    return this.request<{ data: import('./types').Task }>(`/api/crm/tasks/${taskId}/status`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getConversationByPhone(phone: string) {
    const encoded = encodeURIComponent(phone);
    return this.request<{
      data: { contact: import('./types').Contact; conversation: import('./types').Conversation };
    }>(`/api/conversations/by-phone?phone=${encoded}`);
  }

  async lookupConversationByPhone(phone: string) {
    const encoded = encodeURIComponent(phone);
    return this.request<{
      data: {
        contact: import('./types').Contact | null;
        conversation: import('./types').Conversation | null;
      };
    }>(`/api/conversations/by-phone?phone=${encoded}&create=false`);
  }

  async getMessages(conversationId: string) {
    return this.request<{ data: import('./types').Message[] }>(
      `/api/conversations/${conversationId}/messages`,
    );
  }

  async getConversationEvents(
    conversationId: string,
    options?: {
      after?: string;
      types?: import('./types').ConversationEventType[];
      limit?: number;
    },
  ) {
    const search = new URLSearchParams();
    const after = options?.after?.trim();
    if (after) {
      search.set('after', after);
    }

    const typeFilters = options?.types?.filter(
      (type): type is import('./types').ConversationEventType =>
        typeof type === 'string' && type.length > 0,
    );
    if (typeFilters && typeFilters.length > 0) {
      search.set('types', [...new Set(typeFilters)].join(','));
    }

    if (typeof options?.limit === 'number' && Number.isFinite(options.limit)) {
      const normalizedLimit = Math.max(1, Math.min(200, Math.round(options.limit)));
      search.set('limit', String(normalizedLimit));
    }

    const query = search.size > 0 ? `?${search.toString()}` : '';
    return this.request<{
      data: import('./types').ConversationEvent[];
      meta?: {
        isDelta: boolean;
        returned: number;
        cursors: {
          latest: string | null;
          oldest: string | null;
        };
        filters: {
          after: string | null;
          eventTypes: import('./types').ConversationEventType[];
          limit: number | null;
        };
      };
    }>(`/api/conversations/${conversationId}/events${query}`);
  }

  async getConversationCalls(conversationId: string) {
    return this.request<{ data: import('./types').CallRecord[] }>(
      `/api/conversations/${conversationId}/calls`,
    );
  }

  async getCallRecordingBlob(callId: string) {
    return this.requestBlob(`/api/calls/${callId}/recording`, {
      method: 'GET',
    });
  }

  async getVoicemails(status?: import('./types').VoicemailQueueStatus) {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<{ data: import('./types').VoicemailQueueItem[] }>(
      `/api/voicemails${query}`,
    );
  }

  async updateVoicemail(
    voicemailId: string,
    payload: {
      queueStatus?: import('./types').VoicemailQueueStatus;
      assignedUserId?: string | null;
    },
  ) {
    return this.request<{ data: import('./types').VoicemailRecord }>(
      `/api/voicemails/${voicemailId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async sendMessage(conversationId: string, body: string) {
    return this.request<{ data: import('./types').Message }>(
      `/api/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ body }),
      },
    );
  }

  async createConversationEvent(
    conversationId: string,
    payload: {
      channel: import('./types').ConversationEventChannel;
      eventType: import('./types').ConversationEventType;
      direction?: import('./types').MessageDirection;
      body?: string;
      metadataJson?: string;
    },
  ) {
    return this.request<{ data: import('./types').ConversationEvent }>(
      `/api/conversations/${conversationId}/events`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  }

  async appendConversationTranscript(
    conversationId: string,
    payload: { text: string; metadataJson?: string },
  ) {
    return this.request<{ data: import('./types').ConversationEvent }>(
      `/api/conversations/${conversationId}/transcript`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  }

  async regenerateConversationInsights(conversationId: string) {
    return this.request<{
      data: import('./types').RegenerateConversationInsightsData;
    }>(`/api/conversations/${conversationId}/insights/regenerate`, {
      method: 'POST',
    });
  }

  async getRagSettings() {
    return this.request<{ data: import('./types').RagSettings }>('/api/rag/settings', {
      method: 'GET',
    });
  }

  async saveRagSettings(payload: Partial<import('./types').RagSettings>) {
    return this.request<{ data: import('./types').RagSettings }>('/api/rag/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async ingestRagConversation(conversationId: string) {
    return this.request<{ status: string; data: { ingested: number } }>(
      `/api/rag/ingest/conversation/${conversationId}`,
      {
        method: 'POST',
      },
    );
  }

  async generateRagSuggestions(payload: {
    conversationId: string;
    prompt?: string;
    draft?: string;
    mode?: 'manual' | 'auto';
  }) {
    return this.request<{
      data: import('./types').RagSuggestion[];
      skipped?: {
        reason: string | null;
      };
    }>('/api/rag/suggest', { method: 'POST', body: JSON.stringify(payload) });
  }

  async getRagSuggestions(conversationId: string, options?: { includeStale?: boolean }) {
    const query = options?.includeStale ? '?includeStale=true' : '';
    return this.request<{ data: import('./types').RagSuggestion[] }>(
      `/api/rag/suggestions/${conversationId}${query}`,
      {
        method: 'GET',
      },
    );
  }

  async submitRagFeedback(payload: {
    suggestionId: string;
    signal: 'accept' | 'reject';
    editedText?: string;
    note?: string;
  }) {
    return this.request<{ data: import('./types').RagFeedback }>('/api/rag/feedback', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getRagTelemetry(lookbackDays: number = 30) {
    const days = Number.isFinite(lookbackDays) ? Math.max(1, Math.round(lookbackDays)) : 30;
    return this.request<{ data: import('./types').RagTelemetryOverview }>(
      `/api/rag/telemetry?lookbackDays=${encodeURIComponent(String(days))}`,
      {
        method: 'GET',
      },
    );
  }

  async getRealtimeTelemetry(lookbackDays: number = 30) {
    const days = Number.isFinite(lookbackDays) ? Math.max(1, Math.round(lookbackDays)) : 30;
    return this.request<{ data: import('./types').RealtimeTelemetryOverview }>(
      `/api/realtime/telemetry?lookbackDays=${encodeURIComponent(String(days))}`,
      {
        method: 'GET',
      },
    );
  }

  async getConversationAiFeedback(conversationId: string) {
    return this.request<{ data: import('./types').AiFeedbackEntry[] }>(
      `/api/conversations/${conversationId}/feedback`,
      {
        method: 'GET',
      },
    );
  }

  async submitConversationAiFeedback(
    conversationId: string,
    payload: {
      targetType: import('./types').AiFeedbackTargetType;
      signal: import('./types').AiFeedbackSignal;
      targetEventId?: string;
      note?: string;
    },
  ) {
    return this.request<{ data: import('./types').AiFeedbackEntry }>(
      `/api/conversations/${conversationId}/feedback`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  }

  async getAiTrainingOverview() {
    return this.request<{ data: import('./types').AiTrainingOverview }>('/api/ai/training', {
      method: 'GET',
    });
  }

  async saveAiTrainingSettings(payload: Partial<import('./types').AiTrainingSettings>) {
    return this.request<{ status: string; data: import('./types').AiTrainingSettings }>(
      '/api/ai/training',
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  async applyAiTrainingRecommendation() {
    return this.request<{ status: string; data: import('./types').AiTrainingRecommendation }>(
      '/api/ai/training/apply',
      {
        method: 'POST',
      },
    );
  }

  async getTwilioToken(identity?: string): Promise<{ token: string }> {
    const query = identity ? `?identity=${encodeURIComponent(identity)}` : '';
    return this.request<{ token: string }>(`/api/twilio/token${query}`, { method: 'GET' });
  }

  async getTeamIntegrations() {
    return this.request<{ data: import('./types').TeamIntegrations }>('/api/team/integrations');
  }

  async getTeamRoutingReadiness() {
    return this.request<{ data: import('./types').TelephonyRoutingReadiness }>(
      '/api/team/integrations/routing-readiness',
    );
  }

  async saveTeamIntegrations(data: import('./types').TeamIntegrations) {
    return this.request<{
      status: string;
      twilioSmsWebhook?: { configured: boolean; webhookUrl?: string; warning?: string };
      twilioVoiceWebhook?: { configured: boolean; webhookUrl?: string; warning?: string };
      telephonyRouting?: import('./types').TeamIntegrations;
      numberInventory?: import('./types').TelephonyNumberInventoryEntry[];
      routingWarnings?: string[];
    }>('/api/team/integrations', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async pushSystemSecrets(secrets: Record<string, string>) {
    return this.request<{ status: string; updated: string[] }>('/api/system/secrets', {
      method: 'PUT',
      body: JSON.stringify({ secrets }),
    });
  }

  async getRealtimeToken() {
    return this.request<{ session_id: string; token: string }>('/api/realtime/token', {
      method: 'GET',
    });
  }

  async getConversationLiveTicket(conversationId: string) {
    const query = new URLSearchParams({ conversationId });
    return this.request<{ data: import('./types').ConversationLiveTicketResponse }>(
      `/api/realtime/live-ticket?${query.toString()}`,
      {
        method: 'GET',
      },
    );
  }

  async getServiceStatus() {
    return this.request<{ data: import('./types').ServiceStatusSnapshot }>('/api/status/services', {
      method: 'GET',
    });
  }

  async getBillingUsage(period: import('./types').BillingPeriod = 'day') {
    const query = new URLSearchParams({ period });
    return this.request<{ data: import('./types').BillingUsageSnapshot }>(
      `/api/billing/usage?${query.toString()}`,
      {
        method: 'GET',
      },
    );
  }

  async sendSms(to: string, body: string, conversationId?: string) {
    return this.request<{ sid: string; status: string; conversationId: string }>(
      '/api/telephony/sms',
      {
        method: 'POST',
        body: JSON.stringify({ to, body, conversationId }),
      },
    );
  }
}
